import { ApolloCache } from "@apollo/client";
import { DutyItem, DutyItemAttr, PilotDuty } from "../Generated/avroGraphqlDuty";
import { State } from "../PilotDuty/PilotDutyPage";
import { GlobalContextValue } from "./Contexts";
import { MaybeExtended } from "./TypeExtensions";
import { IsValidNumber, NumberOrZeroIfInvalid } from "./UtilityMethods";
import { ValidatePilotDuty } from "./ValidatePilotDuty";
import dayjs from "dayjs";

export enum DutyItemAttrTypesEnum {
    night_takeoffs = 1,
    total_takeoffs = 2,
    night_landings = 3,
    total_landings = 4,
    ifr_hours = 5,
    ifr_approaches = 6,
    rnp_approaches = 7,
    rnp_success = 8,
}

export const handleAddItemClicked = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    localState: State,
    setLocalState: (arg0: State) => void,
    context: GlobalContextValue
): void => {
    e.preventDefault();

    const newDutyItem = {
        id: localState.LocalId,
        aircraft_type_id: null,
        duty_type_id: null,
        duty_start: null,
        location_start: "",
        duty_end: null,
        location_end: "",
        flight_time: 0,
    } as DutyItem;

    let newDutyItemsList = [] as DutyItem[];
    let updatedPilotDuty = {};

    if (localState.PilotDuty) {
        newDutyItemsList = [...(localState.PilotDuty?.DutyItems as DutyItem[]), newDutyItem] as DutyItem[];
        updatedPilotDuty = { ...localState.PilotDuty, DutyItems: newDutyItemsList };
    } else {
        const newPilotDuty = {
            person_id: Number(context.loggedInUser.id),
            entry_date: context.selectedDate,
            DutyItems: [],
            is_split: 0,
            id: null,
        } as any as PilotDuty;
        newDutyItemsList = [...(newPilotDuty?.DutyItems as DutyItem[]), newDutyItem] as DutyItem[];
        updatedPilotDuty = { ...newPilotDuty, DutyItems: newDutyItemsList };
    }
    const errors = ValidatePilotDuty(updatedPilotDuty as PilotDuty);
    setLocalState({ ...localState, LocalId: localState.LocalId - 1, PilotDuty: updatedPilotDuty as PilotDuty, Errors: errors });
};

export const handleRemoveItemClicked = (indexToDelete: number, localState: State, setLocalState: (arg0: State) => void): void => {
    if (localState.PilotDuty.DutyItems) {
        if (localState.PilotDuty.id && localState.PilotDuty.DutyItems.length === 1) {
            setLocalState({ ...localState, ShowDeleteDialog: true });
        } else {
            const newDutyItemList = [
                ...localState.PilotDuty.DutyItems.slice(0, indexToDelete),
                ...localState.PilotDuty.DutyItems.slice(indexToDelete + 1),
            ] as DutyItem[];
            const updatedPilotDuty = { ...localState.PilotDuty, DutyItems: newDutyItemList };

            const errors = ValidatePilotDuty(updatedPilotDuty);
            setLocalState({ ...localState, HasChanges: true, PilotDuty: updatedPilotDuty, Errors: errors });
        }
    }
};

export const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    pilotDutyItemProperty: string,
    dutyItemId: number,
    currentValue: string | MaybeExtended<number> | undefined,
    localState: State,
    setLocalState: (arg0: State) => void
): void => {
    let inputValue: string | number = e.target.value;

    if (currentValue === inputValue) {
        setLocalState({ ...localState, HasChanges: false });
    } else {
        if (pilotDutyItemProperty === "flight_time") {
            if (!IsValidNumber(inputValue, 1)) return;
        } else if (pilotDutyItemProperty === "location_start" || pilotDutyItemProperty === "location_end") inputValue = inputValue.toLocaleUpperCase();
        else inputValue = isNaN(Number(inputValue)) ? Number(currentValue) : Number(inputValue);

        let newDutyItemsList;
        if (pilotDutyItemProperty === "flight_time" || pilotDutyItemProperty === "location_start" || pilotDutyItemProperty === "location_end") {
            const updatedDutyItems =
                localState.PilotDuty &&
                localState.PilotDuty.DutyItems &&
                localState.PilotDuty.DutyItems.map((l) => (l && l.id === dutyItemId ? { ...l, [pilotDutyItemProperty]: inputValue } : l));
            newDutyItemsList = updatedDutyItems;
        } else {
            if (!IsValidNumber(inputValue, 1)) return;

            newDutyItemsList =
                localState.PilotDuty &&
                localState.PilotDuty.DutyItems &&
                localState.PilotDuty.DutyItems.map((dutyItem) => {
                    let updatedDutyItem;
                    if (dutyItem && dutyItem.id === dutyItemId) {
                        let found = false;
                        const updatedItemAttributes = dutyItem.ItemAttributes?.map((itemAttr) => {
                            let updatedItemAttr = itemAttr;
                            if (itemAttr && itemAttr.DutyAttrType?.attr === pilotDutyItemProperty) {
                                if (itemAttr) {
                                    found = true;
                                    updatedItemAttr = {
                                        id: itemAttr.id,
                                        duty_item_id: itemAttr.duty_item_id,
                                        duty_attr_type_id: itemAttr.duty_attr_type_id,
                                        value: Number(inputValue),
                                    };
                                }
                            }

                            return updatedItemAttr;
                        }) as DutyItemAttr[];
                        if (found) {
                            updatedDutyItem = { ...dutyItem, ItemAttributes: updatedItemAttributes };
                        } else {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            const dutyAttrTypeIdToAdd = DutyItemAttrTypesEnum[pilotDutyItemProperty as keyof DutyItemAttrTypesEnum];
                            const addedItemAttr = {
                                duty_attr_type_id: dutyAttrTypeIdToAdd,
                                value: Number(inputValue),
                            };
                            const itemAttributes = (dutyItem.ItemAttributes && (dutyItem.ItemAttributes.slice() as any)) ?? [];
                            const addedItemAttributes = itemAttributes.filter((item: any) => item.duty_attr_type_id !== dutyAttrTypeIdToAdd);
                            addedItemAttributes.push(addedItemAttr);

                            updatedDutyItem = { ...dutyItem, ItemAttributes: addedItemAttributes };
                        }
                    } else updatedDutyItem = dutyItem;
                    return updatedDutyItem as DutyItem;
                });
        }
        setLocalState({ ...localState, HasChanges: true, PilotDuty: { ...localState.PilotDuty, DutyItems: newDutyItemsList } });
    }
};

export const onBlur = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    pilotDutyItemProperty: string,
    id: number,
    localState: State,
    setLocalState: (arg0: State) => void
): void => {
    e.preventDefault();
    if (localState.HasChanges) {
        let updatedPilotDuty;
        if (pilotDutyItemProperty === "flight_time") {
            const updatedDutyItems =
                localState.PilotDuty &&
                localState.PilotDuty.DutyItems &&
                (localState.PilotDuty.DutyItems.map((l) =>
                    l && l.id === id
                        ? {
                              ...l,
                              [pilotDutyItemProperty]: Number(Math.abs(NumberOrZeroIfInvalid(l.flight_time)).toFixed(1)), // eliminate negatives and truncate to one decimal place if they do enter a valid number
                          }
                        : l
                ) as DutyItem[]);
            updatedPilotDuty = { ...localState.PilotDuty, DutyItems: updatedDutyItems } as PilotDuty;
        } else {
            updatedPilotDuty = localState.PilotDuty as PilotDuty;
        }

        const errors = ValidatePilotDuty(localState.PilotDuty);
        setLocalState({ ...localState, Errors: errors, PilotDuty: updatedPilotDuty });
    }
};

// hack required to allow clearing a time on the iPad
export const handleTimeEditOnBlur = (timeField: string, dutyItemId: number, value: string, localState: State, setLocalState: (arg0: State) => void): void => {
    if (value === "" || value === undefined) {
        handleTimeChange(timeField, dutyItemId, null, localState, setLocalState);
    }
};

export const handleTimeChange = (timeField: string, dutyItemId: number, value: Date | null, localState: State, setLocalState: (arg0: State) => void): void => {
    const newDutyItemList =
        localState.PilotDuty.DutyItems &&
        (localState.PilotDuty.DutyItems.map((l) => (l && l.id === dutyItemId ? { ...l, [timeField]: value ?? "" } : l)) as DutyItem[]);
    const updatedPilotDuty = { ...localState.PilotDuty, DutyItems: newDutyItemList } as PilotDuty;
    const errors = ValidatePilotDuty(updatedPilotDuty);
    setLocalState({ ...localState, HasChanges: true, Errors: errors, PilotDuty: updatedPilotDuty });
};

export const clearCacheForFutureSelectedDates = (cache: ApolloCache<any>, context: GlobalContextValue): void => {
    for (const dateToClear of context.selectedDatesList) {
        if (dayjs(context.selectedDate).isBefore(dayjs(dateToClear))) {
            cache.evict({
                id: "ROOT_QUERY",
                fieldName: "getNightCurrencyByPersonAndDate",
                args: {
                    person_id: Number(context.loggedInUser.id),
                    date: dateToClear,
                },
            });
            cache.evict({
                id: "ROOT_QUERY",
                fieldName: "getIfrCurrencyByPersonAndDate",
                args: {
                    person_id: Number(context.loggedInUser.id),
                    date: dateToClear,
                },
            });
            cache.evict({
                id: "ROOT_QUERY",
                fieldName: "getTolCurrencyByPersonAndDate",
                args: {
                    person_id: Number(context.loggedInUser.id),
                    date: dateToClear,
                },
            });
            cache.evict({
                id: "ROOT_QUERY",
                fieldName: "getDutyTimesSummary",
                args: {
                    person_id: Number(context.loggedInUser.id),
                    date: dateToClear,
                },
            });
            cache.gc();
        }
    }
};
